import { Link } from "react-router-dom";
import { Stepper } from "rizzui";
import { WidgetCard } from "@steelyard/theme";
import { tierPath } from "../lib/paths";

/**
 * "How tier pricing works", in the four steps it actually happens in (2026-09-29 — the owner found
 * the tier, rule and generate screens "hard to understand how it will work"). Shown at the top of
 * each pricing screen with the screen's own step current, so a reader always sees where they are in
 * the chain and can step to its neighbours. The template's own `WidgetCard` holding rizzui's
 * `Stepper`, horizontal — no card or step markup of ours.
 *
 * Every sentence here is checked against the backend, not assumed:
 *  - base prices ARE the Default Tier's rows (`SetBasePricesHandler`, spec §9.1);
 *  - a tier's prices are generated from them by its rules; no matching rule means no discount
 *    (`GenerateTierPricesHandler`: `winner?.DiscountPct ?? DiscountPercentage.Zero`);
 *  - generating writes rows and leaves hand-edited ones alone (pricing-admin.md, FR-045);
 *  - customers see the tier's generated or hand-set prices (pricing-consumer.md).
 */
export type PricingStep = "base" | "rules" | "generate" | "customers";

const STEPS: { key: PricingStep; title: string; description: string }[] = [
  { key: "base", title: "Base prices", description: "The Default Tier's price, set per SKU and unit." },
  { key: "rules", title: "Discount rules", description: "A % off the base price, for all, a brand, category or SKU." },
  { key: "generate", title: "Generate", description: "Writes this tier's price list from the rules." },
  { key: "customers", title: "Customers", description: "On a customer's page, Assign terms → this tier. They then pay these prices." },
];

/** `current` highlights the screen's own step; without it (the tier list, a tier's settings) every step is a plain link. */
export function PricingFlow({ current, tierSlug }: { current?: PricingStep; tierSlug?: string }) {
  const href: Partial<Record<PricingStep, string>> = {
    base: "/catalogue/skus",
    rules: tierSlug ? `${tierPath(tierSlug)}/rules` : undefined,
    generate: tierSlug ? `${tierPath(tierSlug)}/prices` : undefined,
    customers: "/customers",
  };
  const currentIndex = STEPS.findIndex((step) => step.key === current);

  return (
    <WidgetCard title="How tier pricing works" titleClassName="text-sm font-semibold">
      <nav aria-label="How tier pricing works" className="mt-4">
      <Stepper currentIndex={currentIndex} className="w-full" titleClassName="text-sm" descriptionClassName="text-xs text-gray-500">
        {STEPS.map((step, index) => {
          const to = href[step.key];
          return (
            <Stepper.Step
              key={step.key}
              size="sm"
              title={
                to && index !== currentIndex ? (
                  <Link to={to} className="hover:underline">
                    {step.title}
                  </Link>
                ) : (
                  <span aria-current={index === currentIndex ? "step" : undefined}>{step.title}</span>
                )
              }
              description={step.description}
            />
          );
        })}
      </Stepper>
      </nav>
    </WidgetCard>
  );
}
